import React from 'react';
import styled from 'styled-components';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Modal styles matching the app's design system
const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'auto',
  minWidth: '400px',
  maxWidth: '600px',
  bgcolor: 'background.paper',
  boxShadow: '0px 11px 15px -7px rgba(169,97,241,0.3), 0px 24px 38px 3px rgba(87,204,244,0.2), 0px 9px 46px 8px rgba(169,97,241,0.15)',
  borderRadius: '8px',
  outline: 'none',
  border: '2px solid transparent',
  backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #a961f1, #57ccf4)',
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box'
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => {
    switch (props.type) {
      case 'success': return 'linear-gradient(135deg, #e8f5e9, #c8e6c9)';
      case 'error': return 'linear-gradient(135deg, #ffebee, #ffcdd2)';
      case 'warning': return 'linear-gradient(135deg, #fff3e0, #ffe0b2)';
      case 'info': return 'linear-gradient(135deg, #f3e5f5, #e1bee7)';
      case 'confirm': return 'linear-gradient(135deg, #e3f2fd, #bbdefb)';
      default: return 'linear-gradient(135deg, #f3e5f5, #e1bee7)';
    }
  }};
  
  svg {
    width: 32px;
    height: 32px;
    color: ${props => {
      switch (props.type) {
        case 'success': return '#4caf50';
        case 'error': return '#f44336';
        case 'warning': return '#ff9800';
        case 'info': return '#a961f1';
        case 'confirm': return '#a961f1';
        default: return '#a961f1';
      }
    }};
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-size: 20px;
  font-weight: 600;
  background: linear-gradient(135deg, #a961f1, #57ccf4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  flex: 1;
`;

const ModalMessage = styled.div`
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #555;
  margin-bottom: 24px;
  white-space: pre-line;
  word-break: break-word;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  text-transform: uppercase;
  min-width: 80px;
  height: 40px;
  padding: 6px 16px;
  border-radius: 4px;
  border: none;
  color: #fff;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12);
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  
  &:hover {
    box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12);
  }
  
  &:active {
    box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12);
  }
  
  &:disabled {
    background-color: rgba(0, 0, 0, 0.12);
    color: rgba(0, 0, 0, 0.26);
    box-shadow: none;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: ${props => {
    switch (props.variant) {
      case 'success': return 'linear-gradient(135deg, #4caf50, #45a049)';
      case 'error': return 'linear-gradient(135deg, #f44336, #da190b)';
      case 'warning': return 'linear-gradient(135deg, #ff9800, #e68900)';
      case 'info': return 'linear-gradient(135deg, #a961f1, #57ccf4)';
      default: return 'linear-gradient(135deg, #a961f1, #57ccf4)';
    }
  }};
  
  &:hover:not(:disabled) {
    background: ${props => {
      switch (props.variant) {
        case 'success': return 'linear-gradient(135deg, #45a049, #388e3c)';
        case 'error': return 'linear-gradient(135deg, #da190b, #c62828)';
        case 'warning': return 'linear-gradient(135deg, #e68900, #d17a00)';
        case 'info': return 'linear-gradient(135deg, #9854e8, #49b8e8)';
        default: return 'linear-gradient(135deg, #9854e8, #49b8e8)';
      }
    }};
    transform: translateY(-1px);
    box-shadow: 0px 4px 8px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 2px 16px 0px rgba(0,0,0,0.12);
  }
`;

const SecondaryButton = styled(Button)`
  background: linear-gradient(135deg, #6c757d, #5a6268);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5a6268, #495057);
    transform: translateY(-1px);
    box-shadow: 0px 4px 8px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 2px 16px 0px rgba(0,0,0,0.12);
  }
`;

// Get icon component based on type
const getIcon = (type) => {
  switch (type) {
    case 'success':
      return <CheckCircleIcon />;
    case 'error':
      return <ErrorIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'info':
      return <InfoIcon />;
    case 'confirm':
      return <HelpOutlineIcon />;
    default:
      return <InfoIcon />;
  }
};

// Get default title based on type
const getDefaultTitle = (type) => {
  switch (type) {
    case 'success':
      return 'Success';
    case 'error':
      return 'Error';
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Information';
    case 'confirm':
      return 'Confirm Action';
    default:
      return 'Alert';
  }
};

/**
 * AlertModal Component
 * 
 * A centralized modal component for displaying alerts and confirmations
 * 
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {string} type - Type of alert: 'success', 'error', 'warning', 'info', 'confirm'
 * @param {string} title - Modal title (optional, defaults based on type)
 * @param {string} message - Main message to display
 * @param {string} confirmText - Text for confirm button (default: 'OK' or 'Confirm')
 * @param {string} cancelText - Text for cancel button (default: 'Cancel', only shown for 'confirm' type)
 * @param {function} onConfirm - Callback when confirm button is clicked
 * @param {function} onCancel - Callback when cancel button is clicked
 * @param {boolean} showCancel - Force show/hide cancel button (default: true for 'confirm', false for others)
 */
const AlertModal = ({
  open,
  onClose,
  type = 'info',
  title,
  message,
  confirmText,
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  showCancel
}) => {
  // Determine if cancel button should be shown
  const shouldShowCancel = showCancel !== undefined ? showCancel : type === 'confirm';
  
  // Get button text
  const primaryButtonText = confirmText || (type === 'confirm' ? 'Confirm' : 'OK');
  
  // Handle confirm action
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };
  
  // Handle cancel action
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };
  
  // Handle backdrop click
  const handleBackdropClick = () => {
    // For confirm dialogs, backdrop click should act like cancel
    if (type === 'confirm') {
      handleCancel();
    } else {
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleBackdropClick}
      aria-labelledby="alert-modal-title"
      aria-describedby="alert-modal-description"
    >
      <Box sx={ModalStyle}>
        <ModalContent>
          <ModalHeader>
            <IconWrapper type={type}>
              {getIcon(type)}
            </IconWrapper>
            <ModalTitle id="alert-modal-title">
              {title || getDefaultTitle(type)}
            </ModalTitle>
          </ModalHeader>
          
          <ModalMessage id="alert-modal-description">
            {message}
          </ModalMessage>
          
          <ModalFooter>
            {shouldShowCancel && (
              <SecondaryButton onClick={handleCancel}>
                {cancelText}
              </SecondaryButton>
            )}
            <PrimaryButton 
              onClick={handleConfirm}
              variant={type === 'confirm' ? 'success' : type}
            >
              {primaryButtonText}
            </PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Box>
    </Modal>
  );
};

export default AlertModal;

